import { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

export type HeatmapPoint = [number, number, number];

interface HeatmapLayerProps {
  points: HeatmapPoint[];
  deviceType: 'mct' | 'sbs' | 'smst' | 'srst' | 'srbm';
}

const sbsGradient = {
  '.0': 'rgba(255,255,255,0)',
  '.1': 'rgb(255,255,204)',
  '.3': 'rgb(199,233,180)',
  '.6': 'rgb(127,205,187)',
  '.8': 'rgb(65,182,196)',
  '.9': 'rgb(44,127,184)',
  '1': 'rgb(37,52,148)',
};

const mctGradient = {
  '.0': 'rgba(28,114,18,0)',
  '.05': 'rgba(26,255,0,1)',
  '.1': 'rgba(208,255,0,1)',
  '.2': 'rgba(230,255,0,1)',
  '.3': 'rgba(247,255,0,1)',
  '.4': 'rgba(255,234,0,1)',
  '.6': 'rgba(255,213,0,1)',
  '.7': 'rgba(255,183,0,1)',
  '.8': 'rgba(255,153,0,1)',
  '.9': 'rgb(255,0,0)',
};

const STGradient = {
  '.0': 'rgba(255,255,255,0)',
  '.1': 'rgb(255,255,204)',
  '.3': 'rgb(199,233,180)',
  '.6': 'rgb(127,205,187)',
  '.8': 'rgb(65,182,196)',
  '.9': 'rgb(44,127,184)',
  '1': 'rgb(37,52,148)',
};

const FLOOR = 0.25;

const scale = (w: number) => Math.log2(w + 1);

const HeatmapLayer = ({ points, deviceType }: HeatmapLayerProps) => {
  const map = useMap();

  const positiveKey = useMemo(
    () =>
      points
        .filter(p => p[2] > 0)
        .map(p => `${p[0]},${p[1]},${p[2]}`)
        .join('|'),
    [points],
  );

  useEffect(() => {
    const positive = points.filter(p => p[2] > 0);
    if (!positive.length) return;

    const scaledMax = Math.max(...positive.map(p => scale(p[2])));

    const normalized: HeatmapPoint[] = positive.map(([lat, lng, w]) => {
      const v = scaledMax === 0 ? 1 : scale(w) / scaledMax;
      return [lat, lng, FLOOR + (1 - FLOOR) * v];
    });

    const gradient =
      deviceType === 'smst' || deviceType === 'srst' ? STGradient : deviceType === 'mct' ? mctGradient : sbsGradient;

    // @ts-ignore
    const heatLayer = (L as any)
      .heatLayer(normalized, {
        radius: 40,
        minOpacity: 0.1,
        blur: 35,
        max: 1,
        maxZoom: -2,
        gradient,
      })
      .addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, positiveKey, deviceType, points]);

  return null;
};

export default HeatmapLayer;
